import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Card,
  Skeleton,
  useColorModeValue,
  Select,
  Flex,
  Text,
} from '@chakra-ui/react';
import ReactECharts from 'echarts-for-react';
import baseUrl from 'Base_Url/baseUrl';
import axiosInstance from 'axiosInstance';

const LineGraph = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const cardBg = useColorModeValue('white', 'gray.800');
  const labelColor = useColorModeValue('#090c10', '#E2E8F0');
  const gridLineColor = useColorModeValue('#CBD5E0', '#4A5568');
  const selectBg = useColorModeValue('white', 'gray.700');
  const selectBorder = useColorModeValue('#CBD5E0', 'gray.600');
  const selectColor = useColorModeValue('black', 'white');
  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/dashboard/year-count`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.data && response.data.status) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error('API fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = useMemo(() => {
    if (!data) return [];
    const dataset = [];
    if (selectedFilter === 'all') {
      dataset.push({ year: 'Before 1996', value: data.before1996 || 0 });
      dataset.push({ year: 'After 1996', value: data.after1996 || 0 });
    } else if (selectedFilter === 'before1996') {
      dataset.push({ year: 'Before 1996', value: data.before1996 || 0 });
    } else if (selectedFilter === 'after1996') {
      dataset.push({ year: 'After 1996', value: data.after1996 || 0 });
    }
    return dataset;
  }, [data, selectedFilter]);

  const chartOptions = useMemo(
    () => ({
      title: {
        text: '',
        left: 'center',
        textStyle: { color: labelColor, fontSize: 18 },
      },
      tooltip: { trigger: 'axis' },
      grid: { left: '5%', right: '5%', bottom: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: filteredData.map((d) => d.year),
        axisLine: { lineStyle: { color: labelColor } },
        axisLabel: { color: labelColor },
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: labelColor } },
        axisLabel: { color: labelColor },
        splitLine: { lineStyle: { type: 'dashed', color: gridLineColor } },
      },
      series: [
        {
          name: 'Encroachments',
          type: 'line',
          smooth: true,
          data: filteredData.map((d) => d.value),
          lineStyle: {
            width: 3,
            color: '#7C3AED',
          },
          itemStyle: {
            color: '#7C3AED',
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: '#D8B4FE' },
                { offset: 1, color: 'rgba(124, 58, 237, 0.1)' },
              ],
            },
          },
          label: {
            show: true,
            position: 'top',
            color: labelColor,
            fontWeight: 'bold',
          },
        },
      ],
    }),
    [filteredData, labelColor, gridLineColor],
  );

  return (
    <Card
      w={{ base: '100%', md: '100%', lg: '100%' }}
      mx="auto"
      h="330px"
      p={2}
      bg={cardBg}
      shadow="md"
      borderRadius="md"
      mt={4}
    >
      <Text
        fontSize="xl"
        fontWeight="bold"
        color={labelColor}
        mb={1}
        textAlign={'center'}
      >
        Encroachment Trend by Year
      </Text>
      <Flex justify="space-between" align="center" mb={1}>
        <Text fontSize="lg" fontWeight="bold" color={labelColor}></Text>
        <Select
          w="150px"
          h={8}
          value={selectedFilter}
          onChange={(e) => setSelectedFilter(e.target.value)}
          bg={selectBg}
          color={selectColor}
          border={`1px solid ${selectBorder}`}
          boxShadow="md"
          _hover={{ borderColor: 'blue.400' }}
          _focus={{
            borderColor: 'blue.400',
            boxShadow: '0 0 0 1px #63B3ED',
          }}
        >
          <option value="all">All</option>
          <option value="before1996">Before 1996</option>
          <option value="after1996">After 1996</option>
        </Select>
      </Flex>

      {loading ? (
        <Skeleton height="300px" borderRadius="md" mt={4} />
      ) : (
        <ReactECharts
          option={chartOptions}
          style={{ height: '300px', width: '100%' }}
        />
      )}
    </Card>
  );
};

export default LineGraph;
